import { useCallback, useEffect, useState } from "react";
import { FetchSaasEntitlements } from "../services/saasEntitlements";
import { SAAS_DEFAULT_ACTOR_ASSIGNMENT, SAAS_FREE_GRANT, SAAS_FREE_CREDIT_POOL } from "../constants/saasPlansCatalog";
import {
  SAAS_ENTITLEMENTS_UPDATED_EVENT,
} from "../utils/saas-entitlements-events";
import { normalizeDefaultActorAssignment } from "../utils/saasDefaultActorAssignment";
import { normalizeFreeCreditPool } from "../utils/saasFreeCreditPool";
import { resolveMinPaygUnlock, resolveBillingCurrency } from "../utils/formatBillingMoney";
import { buildDefaultClientFreeCaps } from "../utils/saasFreeCapsDefaults";

/** Free-plan default matching prototype TV_SAAS.freeGrant. */
export const DEFAULT_FREE_ENTITLEMENTS = Object.freeze({
  plan: "free",
  planDisplayName: "Free",
  isPremium: false,
  isEnterprise: false,
  creditsBalance: SAAS_FREE_CREDIT_POOL.accountWallet,
  creditsAvailable: SAAS_FREE_CREDIT_POOL.accountWallet,
  creditsPurchased: 0,
  creditsUsed: 0,
  freeGrant: Object.freeze({
    credits: SAAS_FREE_GRANT.credits,
    avatarTemplateTries: SAAS_FREE_GRANT.avatarTemplateTries,
  }),
  freeCreditPool: Object.freeze({
    totalGranted: SAAS_FREE_CREDIT_POOL.total,
    totalAvailable: SAAS_FREE_CREDIT_POOL.total,
    account: Object.freeze({
      granted: SAAS_FREE_CREDIT_POOL.accountWallet,
      available: SAAS_FREE_CREDIT_POOL.accountWallet,
    }),
    actors: SAAS_FREE_CREDIT_POOL.actorAllocations.map((row) => Object.freeze({
      defaultKey: row.defaultKey,
      actorName: row.actorName,
      granted: row.creditsAssigned,
      available: row.creditsAssigned,
    })),
  }),
  defaultActorAssignment: Object.freeze({
    grantCredits: SAAS_DEFAULT_ACTOR_ASSIGNMENT.grantCredits,
    settledFromPurchase: false,
    actorAllocations: SAAS_DEFAULT_ACTOR_ASSIGNMENT.actorAllocations,
  }),
  avatarTemplateTriesUsed: 0,
  avatarTemplateTriesRemaining: 1,
  canGenerate: true,
  canTryAvatarTemplate: true,
  canPreview: true,
  freeCaps: Object.freeze(buildDefaultClientFreeCaps()),
  minPaygUnlockInr: 1000,
  minPaygUnlock: 1000,
  minCommitment: 1000,
  currency: "INR",
  country: "IN",
});

export const normalizeEntitlements = (raw, { previous = null } = {}) => {
  if (!raw || raw.error) {
    // Never paint Free starter chrome when we already knew PAYG/Enterprise.
    if (previous && (previous.isPremium || previous.isEnterprise || previous.plan === "premium" || previous.plan === "enterprise")) {
      return {
        ...previous,
        entitlementsUnavailable: true,
        error: raw?.error || "Failed to load SaaS entitlements",
      };
    }
    return { ...DEFAULT_FREE_ENTITLEMENTS, entitlementsUnavailable: Boolean(raw?.error) };
  }
  const plan = String(raw.plan || "free").toLowerCase();
  const isEnterprise = plan === "enterprise";
  const isPremium =
    isEnterprise ||
    plan === "premium" ||
    Boolean(raw.lifetimePremiumUnlock || raw.lifetimePaidUnlock);
  const freeGrant = {
    credits: Number(
      raw.freeGrantCredits
        ?? raw.freeGrant?.credits
        ?? SAAS_FREE_GRANT.credits
    ),
    avatarTemplateTries: Number(
      raw.avatarTemplateTriesTotal ?? raw.freeGrant?.avatarTemplateTries ?? SAAS_FREE_GRANT.avatarTemplateTries
    ),
  };
  const defaultActorAssignment = normalizeDefaultActorAssignment(
    raw.defaultActorAssignment
  ) || {
    grantCredits: SAAS_DEFAULT_ACTOR_ASSIGNMENT.grantCredits,
    settledFromPurchase: false,
    actorAllocations: SAAS_DEFAULT_ACTOR_ASSIGNMENT.actorAllocations,
  };
  const creditsBalance = Number(raw.creditsBalance ?? raw.credits_balance) || 0;
  const creditsAvailable =
    Number(raw.creditsAvailable ?? raw.credits_available ?? creditsBalance) || 0;
  const creditsPurchased =
    Number(raw.creditsPurchased ?? raw.credits_purchased) || 0;
  const explicitUsed = raw.creditsUsed ?? raw.credits_used;
  const creditsUsed =
    explicitUsed != null && explicitUsed !== ""
      ? Number(explicitUsed) || 0
      : Math.max(0, creditsPurchased - creditsAvailable);
  const avatarTemplateTriesUsed = Number(raw.avatarTemplateTriesUsed) || 0;
  const avatarTemplateTriesRemaining = Number(
    raw.avatarTemplateTriesRemaining ??
      Math.max(0, freeGrant.avatarTemplateTries - avatarTemplateTriesUsed)
  );
  const currency = resolveBillingCurrency(raw);
  const minPaygUnlock = resolveMinPaygUnlock({
    currency,
    country: raw.country,
    minPaygUnlock: raw.minPaygUnlock,
    minCommitment: raw.minCommitment,
    minPaygUnlockInr: raw.minPaygUnlockInr,
  });

  const freeCreditPool = normalizeFreeCreditPool(raw.freeCreditPool);
  const freePoolAvailable = freeCreditPool?.totalAvailable ?? 0;
  const freeCaps = isPremium
    ? raw.freeCaps || null
    : raw.freeCaps?.remaining
      ? raw.freeCaps
      : buildDefaultClientFreeCaps(raw.freeCaps?.usage || {});

  return {
    plan: isPremium ? (isEnterprise ? "enterprise" : "premium") : "free",
    planDisplayName: isEnterprise
      ? "Enterprise"
      : isPremium
        ? "Pay as you go"
        : "Free",
    isPremium,
    isEnterprise,
    creditsBalance,
    creditsAvailable,
    creditsPurchased,
    creditsUsed,
    freeGrant,
    freeCreditPool,
    defaultActorAssignment,
    avatarTemplateTriesUsed,
    avatarTemplateTriesRemaining,
    canGenerate: isPremium
      ? creditsAvailable > 0
      : Boolean(raw.freeCaps?.applies !== false) && freePoolAvailable > 0,
    canTryAvatarTemplate: isPremium
      ? creditsAvailable > 0
      : Boolean(raw.freeCaps?.applies !== false)
        && freePoolAvailable > 0
        && avatarTemplateTriesRemaining > 0,
    canPreview: raw.canPreview !== false,
    freeCaps,
    freePlanCaps: raw.freePlanCaps || null,
    minPaygUnlockInr: raw.minPaygUnlockInr != null ? Number(raw.minPaygUnlockInr) : (currency === "USD" ? null : 1000),
    minPaygUnlock,
    minCommitment: minPaygUnlock,
    currency,
    country: raw.country || (currency === "USD" ? "US" : "IN"),
    entitlementsUnavailable: false,
  };
};

export const useSaasEntitlements = (accountId) => {
  const [entitlements, setEntitlements] = useState({ ...DEFAULT_FREE_ENTITLEMENTS });
  const [isLoading, setIsLoading] = useState(() => Boolean(accountId));

  const refresh = useCallback(async () => {
    if (!accountId) {
      setEntitlements({ ...DEFAULT_FREE_ENTITLEMENTS });
      return;
    }
    setIsLoading(true);
    let response;
    try {
      response = await FetchSaasEntitlements(accountId);
    } catch (error) {
      response = { error: error?.message || "Failed to load SaaS entitlements" };
    }
    setEntitlements((prev) => normalizeEntitlements(response, { previous: prev }));
    setIsLoading(false);
  }, [accountId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!accountId || typeof window === "undefined") return undefined;
    const onUpdated = (event) => {
      const updatedAccountId = event?.detail?.accountId;
      if (updatedAccountId != null && String(updatedAccountId) === String(accountId)) {
        refresh();
      }
    };
    window.addEventListener(SAAS_ENTITLEMENTS_UPDATED_EVENT, onUpdated);
    return () => {
      window.removeEventListener(SAAS_ENTITLEMENTS_UPDATED_EVENT, onUpdated);
    };
  }, [accountId, refresh]);

  return { entitlements, isLoading, refresh };
};

export default useSaasEntitlements;
